import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

import {
  ILocationPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/LocationPropertyTypeStrategy/types/ILocationPropertyTypeInterfaces'

import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {IFilterItem} from '@toolify/server/src/services/FilterService/types/IFilterItem'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {GroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/types/GroupGranularityType'
import {
  getLocationBucketLevel,
  locationGroupGranularityDescriptor,
} from '@toolify/client/src/modules/databases/utils/locationGroupGranularityDescriptor'
import {
  isLocationBucket,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/LocationPropertyTypeStrategy/locationBucket'

export function useLocationValueTypeStrategy() {
  const strategyComponentType = PropertyStrategyComponentType.Input

  const fullValueToText = (value: ILocationPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance) => {
    return value?.displayAddress || ''
  }

  const getHrefFromValue = (value: ILocationPropertyTypeInterfaces['fullValue']) => {
    if (!value || !value.coordinates) {
      return ''
    }
    const {coordinates} = value
    if (!Array.isArray(coordinates) || coordinates.length !== 2) {
      return ''
    }
    const [lng, lat] = coordinates
    return `https://www.mapbox.com/maps/?center=${lng},${lat}&zoom=14&layers=points`
  }

  const getTitleText = (value: ILocationPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance) => {
    return 'Location'
  }

  const getFormulaValue = (fullValue: ILocationPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance) => {
    return JSON.stringify(fullValue)
  }

  const fullValueToInputValue = (value: ILocationPropertyTypeInterfaces['fullValue']) => {
    if(!value) {
      return null
    }
    return {
      displayAddress: value.displayAddress || '',
      address: value.address || '',
      coordinates: value.coordinates || [0, 0],
      addressComponents: value.addressComponents || {
        country: null,
        state: null,
        county: null,
        commune: null,
        city: null,
        street: null,
        postalCode: null,
      },
    }
  }

  const hasStoredCoordinates = (value: unknown): boolean => {
    return Array.isArray((value as Partial<ILocationPropertyTypeInterfaces['fullValue']> | null)?.coordinates)
  }

  const storageValueToInputValue = (value: unknown): ILocationPropertyTypeInterfaces['inputValue'] | null => {
    if(!value || isLocationBucket(value) || !hasStoredCoordinates(value)) {
      return null
    }
    return fullValueToInputValue(value as ILocationPropertyTypeInterfaces['fullValue'])
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[]): IFilter => {
    const subItems: IFilterItem[] = databaseProperties.map(databaseProperty => {
      const subProperties = ['address', 'displayAddress']
      return subProperties.map(subProperty => {
        const filterItem: IFilterItem = {
          type: FilterItemType.Property,
          id: databaseProperty.id,
          subProperty,
          operator: {
            type: FilterPropertyOperator.RegExp,
          },
          payload: {
            type: FilterPropertyPayloadType.Static,
            value: search,
          },
          isDisabled: false,
        }
        return filterItem
      })
    }).flat(1)
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: subItems,
    }
  }

  const compareItemValues = (
    valueA: ILocationPropertyTypeInterfaces['fullValue'],
    valueB: ILocationPropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
  ): number => {
    const hasValueA = valueA && (valueA.address || valueA.displayAddress)
    const hasValueB = valueB && (valueB.address || valueB.displayAddress)
    
    if (!hasValueA && !hasValueB) return 0
    if (!hasValueA) return 1
    if (!hasValueB) return -1
    
    const addressA = valueA.displayAddress || valueA.address || ''
    const addressB = valueB.displayAddress || valueB.address || ''
    let comparison = addressA.localeCompare(addressB)
    
    if (comparison === 0) {
      const displayAddressA = valueA.displayAddress || ''
      const displayAddressB = valueB.displayAddress || ''
      comparison = displayAddressA.localeCompare(displayAddressB)
    }
    
    return sortDirection === '1' ? comparison : -comparison
  }

  const sortGroupItems = (
    a: {fullValue: ILocationPropertyTypeInterfaces['fullValue']},
    b: {fullValue: ILocationPropertyTypeInterfaces['fullValue']},
  ): number => {
    return compareItemValues(a.fullValue, b.fullValue, '1')
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table

  const canMoveItemToAnotherGroup = (property: IDatabasePropertyResponseInstance | null | undefined, granularity?: GroupGranularityType | null): boolean => {
    if(property?.canBeModified === false) {
      return false
    }
    return getLocationBucketLevel(granularity) === null
  }

  const canCreateItemInGroup = (property: IDatabasePropertyResponseInstance | null | undefined, granularity?: GroupGranularityType | null, groupValue?: unknown): boolean => {
    if(property?.canBeModified === false) {
      return false
    }
    if(getLocationBucketLevel(granularity) !== null) {
      return false
    }
    return groupValue === null || groupValue === undefined || hasStoredCoordinates(groupValue)
  }

  const groupGranularity = locationGroupGranularityDescriptor

  return {
    fullValueToText,
    strategyComponentType,
    getTitleText,
    getHrefFromValue,
    getFormulaValue,
    fullValueToInputValue,
    storageValueToInputValue,
    searchToFilterQuery,
    compareItemValues,
    sortGroupItems,
    canGroupByView,
    canMoveItemToAnotherGroup,
    canCreateItemInGroup,
    groupGranularity,
  }
}
